import { useCallback, useEffect } from 'react';

import {
  Box,
  IconButton,
  makeStyles,
  Drawer,
  alpha,
  Typography,
  Divider,
  useTheme,
} from '@material-ui/core';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';

import { MediaTypeConfig } from 'constants/mediaTypes';
import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  contatosSelector,
  fetchMidiasContato,
  MidiasContatoEnum,
  resetMidiasContato,
} from 'store/reducers/contatos';
import {
  atualizarDocumentosAtendimento,
  AtualizarMidiaMensagemInterface,
  baixandoMensagemDocumentosAtendimento,
  BaixandoMidiaMensagemInterface,
} from 'store/reducers/mensagens';

import ListaDocumentosContato from './ListaDocumentosContato';
import ListaMidiasContato from './ListaMidiasContato';

const useStyles = makeStyles(({ appDrawer, spacing, palette }) => ({
  drawer: {
    width: appDrawer.minWidth,
  },
  drawerPaper: {
    width: appDrawer.minWidth,
    flexShrink: 0,
    overflow: 'hidden',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    padding: spacing(2),
  },
  iconContainer: {
    width: 50,
    height: 50,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  list: {
    height: '100%',
    overflow: 'auto',
    padding: spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(1),
  },
  contentItem: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    minWidth: spacing(10),
    maxWidth: spacing(10),
  },
  image: {
    width: '100%',
    borderRadius: spacing(1),
  },
  iconButton: {
    color: palette.getContrastText(alpha(palette.common.black, 0.4)),
    backgroundColor: `${alpha(palette.common.black, 0.4)} !important`,
    position: 'absolute',
    top: '35%',
    left: '35%',
  },
  background: {
    borderRadius: spacing(1),
    color: palette.common.white,
    backgroundColor: alpha(palette.common.black, 0.68),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
  },
}));

type DrawerMidiasContatoProps = {
  open: boolean;
  onClose: () => void;
  contatoId: string;
  canalId: string;
  mediaType: MediaTypeConfig;
};

export default function DrawerMidiasContato(props: DrawerMidiasContatoProps) {
  const { open, onClose, canalId, contatoId, mediaType } = props;
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const IconComponent = mediaType.icon;
  const color = theme.palette[mediaType.colorKey].main;

  const { midiasContato, loadingMidiasContato, midiasContatoCursor } =
    useAppSelector(contatosSelector);

  const getMidiasContato = useCallback(
    (params?: { cursor?: CursorInterface }) => {
      if (!contatoId || !canalId) return;

      dispatch(
        fetchMidiasContato({
          contatoId,
          channelCode: canalId,
          type: mediaType.key,
          cursor: params?.cursor,
        }),
      );
    },
    [canalId, contatoId, dispatch, mediaType.key],
  );

  const fetchNextMidiasContato = useCallback(() => {
    const { afterCursor } = midiasContatoCursor;

    if (afterCursor) {
      getMidiasContato({
        cursor: { afterCursor },
      });
    }
  }, [getMidiasContato, midiasContatoCursor]);

  useEffect(() => {
    if (open && contatoId && canalId) {
      getMidiasContato();
    }
  }, [open, canalId, contatoId, getMidiasContato]);

  useEffect(() => {
    const onBaixandoMidiaMensagem = (data: BaixandoMidiaMensagemInterface) => {
      dispatch(baixandoMensagemDocumentosAtendimento(data));
    };
    const onAtualizarMidiaMensagem = (
      data: AtualizarMidiaMensagemInterface,
    ) => {
      dispatch(atualizarDocumentosAtendimento(data));
    };

    socket.on(socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM, onBaixandoMidiaMensagem);
    socket.on(
      socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
      onAtualizarMidiaMensagem,
    );

    return () => {
      socket.off(
        socketEvents.ON_BAIXANDO_MIDIA_MENSAGEM,
        onBaixandoMidiaMensagem,
      );
      socket.off(
        socketEvents.ON_ATUALIZAR_MIDIA_MENSAGEM,
        onAtualizarMidiaMensagem,
      );
    };
  }, [dispatch]);

  const fileCount = midiasContato.length;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={classes.drawer}
      classes={{ paper: classes.drawerPaper }}
      BackdropProps={{
        onExited: () => {
          dispatch(resetMidiasContato());
        },
      }}
    >
      <Box className={classes.headerContent}>
        <IconButton onClick={onClose}>
          <ArrowBackIosIcon style={{ width: 16, height: 16 }} />
        </IconButton>

        <Box
          className={classes.iconContainer}
          style={{
            backgroundColor: alpha(color, 0.1),
            color,
          }}
        >
          <IconComponent />
        </Box>

        <Box>
          <Typography
            variant="body2"
            color="textPrimary"
            style={{ fontWeight: 500 }}
          >
            {mediaType.title}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {fileCount} {fileCount === 1 ? 'arquivo' : 'arquivos'}
          </Typography>
        </Box>
      </Box>

      <Divider />

      {mediaType.key === MidiasContatoEnum.DOCUMENTO ? (
        <ListaDocumentosContato
          documentos={midiasContato}
          loading={loadingMidiasContato}
          hasMore={Boolean(midiasContatoCursor?.afterCursor)}
          loadMore={fetchNextMidiasContato}
        />
      ) : (
        <ListaMidiasContato
          midias={midiasContato}
          loading={loadingMidiasContato}
          hasMore={Boolean(midiasContatoCursor?.afterCursor)}
          loadMore={fetchNextMidiasContato}
          columns={5}
        />
      )}
    </Drawer>
  );
}
